import React, { useState, useEffect, useMemo } from 'react';
import { SearchBar } from 'src/components/input';
import styles from './styles.module.scss';
import moment from 'moment';
import { flatMap, get, groupBy, map, size } from 'lodash';
import { ActivityRow, DetailedViewPlaceHolder, NormalViewPlaceHolder } from './components/ActivityRow';
import { ActivityFilterDropDown } from './components/ActivityFilterDropDown';
import { ActivitiesRequestPayload, Activities } from 'src/types/dealInsights';
import InfiniteScroll from 'src/components/infiniteScroll';
import cx from 'classnames';

import { useSpring, animated } from '@react-spring/web';
import { useDealActivities } from 'src/query/dealInsights/dealInsights';
import { activityFilterOptions } from 'src/utils/dealInsights.helper';


interface Props {
  isDetailedView?: boolean;
  dealId?: string | null,
  classnames?: string,
  title: string,
}

const intialRequestFilters: ActivitiesRequestPayload = {
  page: 1,
  size: 20,
}

const emptyImg = '/images/dealInsights/noActivity.svg';

const NoResultsFound = ({ msg }: { msg: string }) => (
  <div className={styles.emptyState}>
    <img src={emptyImg} alt="No results" className={styles.emptyStateImg} />
    <p className={styles.emptyStateMsg}>{msg}</p>
  </div>
);

export const DealActivity = ({ isDetailedView = false, dealId, classnames, title }: Props) => {

  // ui state's
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<Record<string, any>>(activityFilterOptions[0]);
  const [toggleSeeMore, setToggleSeeMore] = useState<boolean>(false);
  const [dealActivities, setDealActivities] = useState<Record<string, any>[]>([]); // list of deal activitities

  // request Payload
  const requestActivitiesFilters = useMemo(() => {
    if (isDetailedView && !dealId) return null; // block payload entirely ( to the query)

    return {
      ...intialRequestFilters,
      ...(dealId && { dealExternalId: dealId }), // dealId
      ...(filterType.value !== 'all'   // filter
        ? { activityType: filterType.value }
        : {}),
    };
  }, [dealId, isDetailedView, filterType]);

  // query's
  const { data: activityRecords, hasNextPage: hasNextPageEvents, loadMore: loadMoreEvents, isFetching: isFetchingRecords, refetch, isRefetching: isRefetchingDealActivities, isLoading: isLoadingDealActivities } = useDealActivities(requestActivitiesFilters, isDetailedView ? !!dealId : true);

  const expandTransitions = useSpring({
    height: toggleSeeMore
      ? 700
      : 400,
    config: { tension: 210, friction: 26 },
  });

  useEffect(() => {
    if (activityRecords && size(get(activityRecords, "pages")) > 0) {
      const listOfRecords = flatMap(activityRecords.pages, (page) => page.activities);

      // clubbing
      const clubbedActivities = Object.values(
        groupBy(listOfRecords, (item: Activities) =>
          moment(item.occurredAt).format("YYYY-MM-DD")
        )
      ).map((items: Activities[]) => ({
        occurredAt: items[0].occurredAt,
        activities: items
      }));
      setDealActivities(clubbedActivities);
    } else {
      setDealActivities([]);
    }
  }, [activityRecords]);


  return (
    <div className={cx(styles.activityContainer, classnames, {
      [styles.noDetailView]: !isDetailedView,
    })}>
      {/* Row 1: title */}
      <h3 className={styles.title}>{title}</h3>

      {/* Row 2: Controls */}
      {isDetailedView && <div className={styles.controlsRow}>
        <div className={styles.searchWrapper}>
          <SearchBar
            query={searchQuery}
            onSearch={(query) => setSearchQuery(query || '')}
          />
        </div>
        <ActivityFilterDropDown
          options={activityFilterOptions}
          selected={filterType}
          onChange={setFilterType}
        />
      </div>}

      {/* Row 3: Activities list */}
      <div className={styles.activityList}>
        {isLoadingDealActivities || isRefetchingDealActivities ? <p className={styles.loading}>
          Loading...
        </p> : <>
          {size(dealActivities) > 0 ? (
            <animated.div
              style={{
                ...expandTransitions,
                overflow: 'hidden',
              }}
            >
              <InfiniteScroll
                loading={isFetchingRecords}
                hasMore={hasNextPageEvents}
                loadMore={loadMoreEvents}
                className={styles.scrollContainer}
                loader={() => <div className={cx(styles.activityItems, styles.loader)}>
                  {isDetailedView ? <DetailedViewPlaceHolder /> : <NormalViewPlaceHolder />}
                </div>}
              >
                {dealActivities.map((act, index) => {
                  if (!act.occurredAt) return null;
                  // formatter
                  const dateStr = moment(act.occurredAt)
                    .local()
                    .calendar(undefined, {
                      sameDay: '[Today]',
                      lastDay: '[Yesterday]',
                      lastWeek: "DD MMM 'YY",
                      nextWeek: "DD MMM 'YY",
                      nextDay: "DD MMM 'YY",
                      sameElse: "DD MMM 'YY"
                    });

                  return (
                    <div key={index} className={styles.activityGroup}>
                      <div className={styles.dateBadge}>{dateStr}</div>
                      {map(act.activities, (activity) => {
                        return (
                          <div className={cx(styles.content,)}>
                            <ActivityRow activity={activity} isDetailedView={isDetailedView} />
                          </div>
                        )
                      })}
                    </div>
                  );
                })}
              </InfiniteScroll>
            </animated.div>
          ) : <>
            <div className={styles.noActivities}>
              <NoResultsFound msg={isDetailedView ? "No activity found on the deal." : "No activity found."} />
            </div>
          </>} </>}
      </div>

      {size(dealActivities) > 0 && <div className={styles.seeLess} onClick={() => setToggleSeeMore(!toggleSeeMore)}>
        <p className={styles.content}> <i className='paperflite-dropdown'></i> see {toggleSeeMore ? 'less' : 'more'}</p>
      </div>}
    </div>
  );
};

export default DealActivity;
